export const BackofficePath = {
  ROOT: '/backoffice',
  TENANTS: '/backoffice/tenants',
  USERS: '/backoffice/users',
} as const;

export const SeriesStatus = {
  pending: 1,
  seg_queued: 2,
  seg_running: 3,
  seg_end: 4,
  seg_confirmed: 5,
  cfd_queued: 6,
  cfd_running: 7,
  cfd_end: 8,
  complete: 9,
  seg_failed: 1000,
  cfd_failed: 1001,
} as const;

export type SeriesStatusId = (typeof SeriesStatus)[keyof typeof SeriesStatus];

export type SeriesStatusColor =
  | 'white'
  | 'yellow'
  | 'red'
  | 'aqua'
  | 'green'
  | 'gray'
  | 'blue';

export type SeriesStatusItem = {
  id: SeriesStatusId;
  name: string;
  color: SeriesStatusColor;
};

export const SERIES_STATUS_ITEMS: SeriesStatusItem[] = [
  { id: SeriesStatus.pending, name: 'Pending', color: 'white' },
  { id: SeriesStatus.seg_queued, name: 'Seg Pending', color: 'white' },
  { id: SeriesStatus.seg_running, name: 'Seg Running', color: 'white' },
  { id: SeriesStatus.seg_end, name: 'Seg End', color: 'aqua' },
  { id: SeriesStatus.seg_confirmed, name: 'Seg Confirmed', color: 'green' },
  { id: SeriesStatus.cfd_queued, name: 'FFR Pending', color: 'yellow' },
  { id: SeriesStatus.cfd_running, name: 'FFR Running', color: 'white' },
  { id: SeriesStatus.cfd_end, name: 'FFR End', color: 'aqua' },
  { id: SeriesStatus.complete, name: 'Complete', color: 'green' },
  { id: SeriesStatus.seg_failed, name: 'Seg Failed', color: 'red' },
  { id: SeriesStatus.cfd_failed, name: 'FFR Failed', color: 'red' },
];

export type Role = {
  id: string;
  name: string;
  permissions: null | string[];
  isAdmin: boolean;
  isService: boolean;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

export type Tenant = {
  id: string;
  name: string;
  isSuper?: boolean;
  usedCount: number;
  remainingCount: number;
  expiredAt: null | string;
  maxRunnerCount: number;
  createdAt: string;
  deletedAt: null | string;
};

export type User = {
  id: string;
  tenantId?: Tenant['id'];
  name?: string;
  email?: string;
  note?: string | null;
  lastLogin?: string | null;
  loginCount?: number;
  userStatus?: {
    id: string;
    name: string;
  };
  user_email?: string;
  user_name?: string;
  firstName?: string;
  lastName?: string;
  login_count?: number;
  password_changed?: string | null;
  role: Role;
  tenant?: Tenant | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type OAuthClientSecret = {
  client: {
    id: number;
    name: string;
    clientId: string;
    tenantId: string;
    createdAt: string;
    updatedAt: string;
  };
  clientSecret: string;
};

export type SeriesItem = {
  id: string;
  tenantId?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  description?: string;
  seriesUID?: string;
  series_instance_uid?: string;
  series_description?: string;
  protocol_name?: string;
  modality?: string;
  seriesImages?: number;
  imageTag?: {
    manufacturer?: string;
    manufacturer_model_name?: string;
    number_of_frames?: string | number;
    depth?: string | number;
  };
  preventDelete?: boolean;
  preventDeleteId?: null | string;
  warningMessage?: string | null;
  seriesDate?: null | string;
  seriesTime?: null | string;
  manufacturer?: string;
  manufacturerModel?: string;
  analysisRequest?: null;
  status?: { id: SeriesStatusId };
  tenantName?: string;
};

export type PageResponse<T> = {
  data: T[];
  hasNextPage?: boolean;
  apiVersion?: string;
};

export type EntityResponse<T> = T & {
  apiVersion?: string;
};

export type SeriesRow = {
  id: string;
  statusId?: SeriesStatusId;
  tenant: string;
  manufacturer: string;
  model: string;
  images: string;
  seriesDate: string;
  description: string;
  createdAt: string;
  isPreventDelete?: boolean;
  warningMessage?: string | null;
};

export type SeriesFilters = {
  tenantId?: string;
  manufacturer?: string;
  manufacturerModel?: string;
  createdAt?: string;
  startDate?: string;
  endDate?: string;
};

export type UserFilters = {
  tenantId?: string;
  user_name?: string;
};

export type CreateTenantBody = {
  name: string;
  maxRunnerCount: number;
  remainingCount: number;
};

export type UpdateTenantBody = CreateTenantBody;

export type CreateUserBody = {
  email: string;
  name: string;
  password: string;
  isGuest: boolean;
  tenantId: string;
  userStatus?: { id: string };
  role?: { id: Role['id'] };
  note?: string;
};

export type UpdateUserBody = Partial<Omit<CreateUserBody, 'password'>> & {
  password?: string;
};

export const UNLIMITED_REMAINING_COUNT = -1;
